import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import NextLinkMui5 from 'components/atoms/NextLink';
import ProgressButton from 'components/atoms/ProgressButton';
import FieldText from 'containers/profile/FieldText';
import FieldZip from 'containers/profile/FieldZip';
import { MobilePhoneMask } from 'containers/profile/MobilePhoneMask';
import { ReferralCodeMask } from 'containers/profile/ReferralCodeMask';
import { FORM_ERROR } from 'final-form';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { Form } from 'react-final-form';
import { DEFAULT_APPLIED_REBATES } from 'recoil/atoms';
import Rest from 'services/rest';
import { getStateByZip } from 'shared/services/geo';
import { CreateConsumerRequest } from 'shared/types/api/createConsumer';
import { GetInTouch } from '../../../components/atoms/GetInTouch';
import LinkMui5 from '../../../components/atoms/Link';
import { AVAILABLE_IN_STATES } from '../../../shared/config';
import { FormAlert } from '../../deal/components/common';
import consumerSignupFormV2Validate from './consumerSignupFormV2Validate';
import { Permission } from 'shared/types/models';
import Checkbox from 'components/molecules/Checkbox';

const rest = new Rest();

const StyledForm = styled('form')(({ theme }) => ({
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(3),
  },
}));

type ConsumerSignupFormV2Props = {
  role?: Permission;
  onSignup?: (req: CreateConsumerRequest) => void;
  showGetInTouch?: boolean;
};

export const ConsumerSignupFormV2: FC<ConsumerSignupFormV2Props> = ({
  onSignup,
  showGetInTouch = true,
  role = Permission.user,
}) => {
  const [isSMSTermsAgreed, setIsSMSTermsAgreed] = useState(false);
  const router = useRouter();

  async function onSubmitForm(values) {
    try {
      const state = getStateByZip(values.zipcode)?.state;
      const email = values.email.trim();
      const user = { ...values, email };
      await rest.createConsumer(
        {
          ...user,
          state,
          rebates_names: DEFAULT_APPLIED_REBATES,
        },
        role,
      );
      onSignup(user);
    } catch (err) {
      const errMsg = err?.message || err.errors[0]?.msg;
      if (errMsg === 'An account with the given email already exists.') {
        router.push('/login');
      } else {
        return { [FORM_ERROR]: errMsg };
      }
    }
  }

  return (
    <Form
      onSubmit={onSubmitForm}
      validate={consumerSignupFormV2Validate}
      mutators={{
        setZipcode: ([zipcode], state, utils) => {
          utils.changeValue(state, 'zipcode', () => zipcode);
        },
      }}
      render={({
        form,
        handleSubmit,
        submitError,
        submitting,
        values,
        errors,
        touched,
        validating,
        pristine,
      }) => (
        <StyledForm
          className={'js-signup-consumer-form-v2'}
          onSubmit={handleSubmit}
          noValidate
        >
          {submitError && <Alert severity='error'>{submitError}</Alert>}
          <FieldText name='first_name' label='First Name' />
          <FieldText name='last_name' label='Last Name' />
          <FieldText name='email' label='Email' />
          <FieldZip setZipcode={form.mutators.setZipcode} />
          {!pristine &&
            touched.zipcode &&
            !validating &&
            !errors.zipcode &&
            !AVAILABLE_IN_STATES.includes(
              getStateByZip(values?.zipcode)?.state,
            ) && (
              <FormAlert severity='info'>
                Sorry but we are not available in your state. Sign up now and we
                will inform you once we are available
              </FormAlert>
            )}
          <FieldText
            InputProps={{
              inputComponent: MobilePhoneMask,
            }}
            name='mobile_phone'
            label='Cell Phone'
          />
          <FieldText
            name='password'
            label='Password'
            type='password'
            autoComplete='password'
          />
          <FieldText
            InputProps={{
              inputComponent: ReferralCodeMask,
            }}
            name='referral_code'
            label='Referral Code'
          />
          <Checkbox
            onChange={(e) => setIsSMSTermsAgreed(e.target.checked)}
            name='sms-terms'
            label={
              <>
                <Typography fontSize={'0.75rem'}>
                  You are agreeing to receive customer care-related messages
                  from AutoBandit. Message frequency may vary. Standard Message
                  and Data Rates may apply. Reply <b>STOP</b> to opt out. Reply{' '}
                  <b>Help</b> for help.
                </Typography>
                <Typography fontSize={'0.75rem'}>
                  <LinkMui5 component={NextLinkMui5} href='/privacy-policy'>
                    Privacy policy
                  </LinkMui5>
                  .{' '}
                  <LinkMui5 component={NextLinkMui5} href='/terms'>
                    Terms
                  </LinkMui5>
                </Typography>
              </>
            }
          />
          <ProgressButton
            className='js-consumer-signup-signup-button'
            color='primary'
            size='medium'
            variant='contained'
            type='submit'
            loading={submitting}
            disabled={!isSMSTermsAgreed}
            fullWidth
          >
            Create account
          </ProgressButton>
          <Box my={2}>
            <Typography variant='body2'>
              Already have an account?{' '}
              <LinkMui5 component={NextLinkMui5} href='/login'>
                Sign In
              </LinkMui5>
            </Typography>
          </Box>
          {showGetInTouch && (
            <Box my={2}>
              <GetInTouch />
            </Box>
          )}
          <Box my={2}>
            <Typography variant='body2'>
              By signing in or creating an account, you agree with our{' '}
              <LinkMui5 component={NextLinkMui5} href='/terms'>
                Terms & Conditions
              </LinkMui5>{' '}
              and{' '}
              <LinkMui5 component={NextLinkMui5} href='/privacy-policy'>
                Privacy policy
              </LinkMui5>
            </Typography>
          </Box>
        </StyledForm>
      )}
    />
  );
};
